import React, { useEffect, useMemo, useState } from "react";
import "./WalletTable.scss";
import { Form, Modal } from "react-bootstrap";
import { QRCode } from "react-qrcode-logo";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CopyIcon, EyeIcon } from "../../../assets/Svg/SvgImages.jsx";
import bitgoIcon from "../../../assets/icons/bitgo.svg";
import metamaskIcon from "../../../assets/icons/metamask.svg";
import CommonTable from "../../../components/common/CommonTable/CommonTable.jsx";
import ReactPagination from "../../../components/common/pagination";
import CustomInput from "../../../components/ui/CustomInput/CustomInput.jsx";
import CommonButton from "../../../components/ui/CommonButton/CommonButton.jsx";
import { copyToClipboard } from "../../../helpers/commonHelper";
import { toastMsg } from "../../../components/common/Toast/Toast";
import { API } from "../../../services/apiService";
import { formatPortfolioUsdAmount, WALLET_LIST_PAGE_SIZE } from "../chartDataUtils";
const WITHDRAW_ADDRESS_MIN_LENGTH = 20;
const WITHDRAW_ADDRESS_MAX_LENGTH = 130;
const WITHDRAW_AMOUNT_MAX_LENGTH = 30;
const WITHDRAW_AMOUNT_INT_DIGITS = 16;
const WITHDRAW_AMOUNT_FRAC_DIGITS = 8;
/** Completed amount: up to 16 integer digits, optional . and up to 8 fraction digits. */
const WITHDRAW_AMOUNT_FINAL_REGEX = new RegExp(`^(?:0|[1-9]\\d{0,${WITHDRAW_AMOUNT_INT_DIGITS - 1}})(?:\\.\\d{1,${WITHDRAW_AMOUNT_FRAC_DIGITS}})?$`);
const normalizeWithdrawIntegerPart = intPart => {
  if (!intPart) return "";
  const capped = intPart.slice(0, WITHDRAW_AMOUNT_INT_DIGITS);
  if (capped === "0" || /^0+$/.test(capped)) return "0";
  const stripped = capped.replace(/^0+/, "");
  return stripped === "" ? "0" : stripped;
};
const normalizeWithdrawAmountInput = raw => {
  if (raw === "") return "";
  if (raw.length > WITHDRAW_AMOUNT_MAX_LENGTH) return null;
  let cleaned = String(raw).replace(/[^\d.]/g, "");
  if (cleaned.startsWith(".")) {
    cleaned = `0${cleaned}`;
  }
  const dotIndex = cleaned.indexOf(".");
  if (dotIndex !== -1) {
    cleaned = cleaned.slice(0, dotIndex + 1) + cleaned.slice(dotIndex + 1).replace(/\./g, "");
  }
  const hasDot = cleaned.includes(".");
  const [intPart = "", fracPart = ""] = cleaned.split(".");
  const intNorm = normalizeWithdrawIntegerPart(intPart);
  const fracTrim = fracPart.slice(0, WITHDRAW_AMOUNT_FRAC_DIGITS);
  if (hasDot) {
    if (cleaned.endsWith(".") && fracTrim === "") {
      return `${intNorm}.`;
    }
    return fracTrim.length ? `${intNorm}.${fracTrim}` : intNorm;
  }
  return intNorm;
};
const finalizeWithdrawAmountOnBlur = value => {
  if (!value) return "";
  let v = value.endsWith(".") ? value.slice(0, -1) : value;
  if (!v) return "";
  const normalized = normalizeWithdrawAmountInput(v);
  if (normalized === null || normalized === "") return normalized ?? "";
  if (!normalized.includes(".")) return normalized;
  const [intPart, fracPart = ""] = normalized.split(".");
  const frac = fracPart.replace(/0+$/, "");
  return frac.length ? `${intPart}.${frac}` : intPart;
};
const formatWithdrawBalance = value => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8
  });
};
const createWithdrawValidationSchema = maxBalance => Yup.object({
  coin: Yup.string().required("Coin is required"),
  address: Yup.string().trim().required("Address is required").min(WITHDRAW_ADDRESS_MIN_LENGTH, `Address must be at least ${WITHDRAW_ADDRESS_MIN_LENGTH} characters`).max(WITHDRAW_ADDRESS_MAX_LENGTH, `Maximum ${WITHDRAW_ADDRESS_MAX_LENGTH} characters`).test("no-spaces", "Address cannot contain spaces", val => val == null || val === "" || !/\s/.test(val)),
  amount: Yup.string().required("Amount is required").max(WITHDRAW_AMOUNT_MAX_LENGTH, `Maximum ${WITHDRAW_AMOUNT_MAX_LENGTH} characters`).test("format", `Use up to ${WITHDRAW_AMOUNT_INT_DIGITS} digits before decimal and ${WITHDRAW_AMOUNT_FRAC_DIGITS} after`, val => !val || WITHDRAW_AMOUNT_FINAL_REGEX.test(val)).test("positive", "Amount must be greater than 0", val => Number(val) > 0).test("max-balance", function maxBalanceTest(val) {
    const max = Number(maxBalance);
    if (!Number.isFinite(max)) return true;
    const amount = Number(val);
    if (amount > max + 1e-12) {
      return this.createError({
        message: `Amount cannot exceed available balance (${formatWithdrawBalance(max)})`
      });
    }
    return true;
  })
});
const displayField = value => {
  if (value == null || value === "") return "-";
  const s = String(value).trim();
  return s || "-";
};
const resolveTokenCount = w => {
  const n = Number(w?.numberOfTokens ?? w?.count_of_token ?? w?.token_count);
  if (Number.isFinite(n) && n >= 0) return n;
  if (Array.isArray(w?.tokenList)) return w.tokenList.length;
  if (Array.isArray(w?.assetList)) return w.assetList.length;
  return 0;
};
const resolveTokensForModal = data => {
  if (Array.isArray(data?.tokenList) && data.tokenList.length) return data.tokenList;
  if (Array.isArray(data?.assetList) && data.assetList.length) return data.assetList;
  return [];
};
const resolveWithdrawCoin = data => {
  const direct = data?.coin ?? data?.parent_coin ?? data?.parentCoin;
  if (direct != null && String(direct).trim() !== "") {
    return String(direct).trim().split(":")[0].toLowerCase();
  }
  const tokens = resolveTokensForModal(data);
  if (tokens.length > 0) {
    const raw = tokens[0]?.coin ?? tokens[0]?.symbol ?? tokens[0]?.asset_name ?? "";
    if (String(raw).trim() !== "") {
      return String(raw).trim().split(":")[0].toLowerCase();
    }
  }
  const name = data?.walletName ?? data?.wallet_name ?? "";
  const parts = String(name).split("-");
  if (parts.length > 1) {
    const last = parts[parts.length - 1].trim();
    if (last) return last.split(":")[0].toLowerCase();
  }
  return "";
};
const resolveWalletRowSpendable = data => {
  const n = Number(data?.spendableBalance ?? data?.spendable_balance ?? data?.balance ?? data?.confirmedBalance ?? data?.confirmed_balance ?? data?.available_balance ?? 0);
  return Number.isFinite(n) ? n : 0;
};
const resolveWalletRowCoinId = data => {
  const raw = data?.coin ?? data?.parent_coin ?? data?.parentCoin;
  if (raw != null && String(raw).trim() !== "") {
    return String(raw).trim().toLowerCase();
  }
  return resolveWithdrawCoin(data);
};
const MIN_WITHDRAWABLE_BALANCE = 1e-12;
const formatChildWithdrawTokenLabel = opt => opt?.coin ? `${opt.coin}` : "";
const getTokenSpendableBalance = token => {
  const n = Number(token?.spendableBalance ?? token?.available_balance ?? token?.balance ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/** Native coin + balance from wallet row; child tokens with spendable balance only. */
const buildWithdrawContext = data => {
  const nativeCoin = resolveWalletRowCoinId(data) || resolveWithdrawCoin(data);
  const nativeBalance = resolveWalletRowSpendable(data);
  const tokens = resolveTokensForModal(data);
  const totalTokenCount = Number(data?.numberOfTokens) >= 0 ? Number(data.numberOfTokens) : tokens.length;
  const childTokenOptions = tokens.map(t => {
    const coin = String(t?.coin ?? t?.symbol ?? "").trim().toLowerCase();
    const balance = getTokenSpendableBalance(t);
    const symbolPart = coin.includes(":") ? coin.split(":")[1] : coin;
    return {
      coin,
      balance,
      symbolLabel: symbolPart ? symbolPart.toUpperCase() : coin
    };
  }).filter(o => o.coin && o.balance > MIN_WITHDRAWABLE_BALANCE).filter(o => o.coin !== nativeCoin).sort((a, b) => b.balance - a.balance);
  return {
    nativeCoin,
    nativeBalance,
    childTokenOptions,
    totalTokenCount,
    network: displayField(data?.network ?? data?.network_name),
    chain: displayField(data?.chain ?? data?.chainName),
    walletType: String(data?.type || "").toLowerCase()
  };
};
const WalletTable = props => {
  const rows = Array.isArray(props.rows) ? props.rows : [];
  const totalCount = typeof props.totalCount === "number" && Number.isFinite(props.totalCount) ? props.totalCount : rows.length;
  const page = Number(props.page) >= 1 ? Number(props.page) : 1;
  const limit = Number(props.limit) >= 1 ? Number(props.limit) : WALLET_LIST_PAGE_SIZE;
  const onPageChange = props.onPageChange;
  const [tokenModal, setTokenModal] = useState({
    show: false,
    walletLabel: "",
    networkLabel: "",
    chainLabel: "",
    walletAddress: "",
    parentCoin: "",
    tokens: [],
    tokenShape: "assetList"
  });
  const [depositModal, setDepositModal] = useState({
    show: false,
    address: "",
    walletLabel: ""
  });
  const [withdrawModal, setWithdrawModal] = useState({
    show: false,
    walletLabel: "",
    mainCoin: "",
    mainBalance: 0,
    childTokenOptions: [],
    totalTokenCount: 0,
    network: "",
    chain: "",
    walletType: ""
  });
  const [useTokenSelect, setUseTokenSelect] = useState(false);
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [withdrawFormCoin, setWithdrawFormCoin] = useState("");
  const availableWithdrawBalance = useMemo(() => {
    if (!withdrawModal.show) return 0;
    if (useTokenSelect && withdrawModal.childTokenOptions.length) {
      const match = withdrawModal.childTokenOptions.find(o => o.coin === withdrawFormCoin);
      return match?.balance ?? 0;
    }
    return withdrawModal.mainBalance;
  }, [withdrawModal.show, withdrawModal.mainBalance, withdrawModal.childTokenOptions, useTokenSelect, withdrawFormCoin]);
  const withdrawBalanceCoinLabel = useMemo(() => {
    if (!withdrawModal.show) return "";
    if (!useTokenSelect) return withdrawModal.mainCoin;
    const coinId = withdrawFormCoin || withdrawModal.childTokenOptions[0]?.coin;
    const match = withdrawModal.childTokenOptions.find(o => o.coin === coinId);
    return formatChildWithdrawTokenLabel(match) || coinId || "";
  }, [withdrawModal.show, withdrawModal.mainCoin, withdrawModal.childTokenOptions, useTokenSelect, withdrawFormCoin]);
  const withdrawValidationSchema = useMemo(() => createWithdrawValidationSchema(availableWithdrawBalance), [availableWithdrawBalance]);
  const withdrawFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      coin: withdrawFormCoin || withdrawModal.mainCoin || "",
      address: "",
      amount: ""
    },
    validationSchema: withdrawValidationSchema,
    onSubmit: async values => {
      setWithdrawSubmitting(true);
      try {
        const response = await API.withdrawWallet({
          coin: values.coin.trim().toLowerCase(),
          amount: String(values.amount).trim(),
          address: values.address.trim()
        });
        if (response?.status === 200) {
          const txId = response?.data?.txId;
          toastMsg.success(response?.message || (txId ? `Withdrawal submitted. Tx: ${txId}` : "Withdrawal process successfully."));
          closeWithdrawModal();
          if (typeof props.onWithdrawSuccess === "function") {
            props.onWithdrawSuccess();
          }
        } else {
          toastMsg.error(response?.message || "Withdrawal request failed. Please try again.");
        }
      } catch (error) {
        toastMsg.error(error?.response?.data?.message || error?.message || "Withdrawal request failed.");
      } finally {
        setWithdrawSubmitting(false);
      }
    }
  });
  useEffect(() => {
    if (withdrawModal.show) {
      withdrawFormik.validateForm();
    }
  }, [availableWithdrawBalance, withdrawValidationSchema, withdrawModal.show]);
  const Tableheading = ["Wallet Type", "Wallet Name", "Network", "Chain", "Address", "No. of Tokens", "Status", "Action"];
  const handlePageChange = ({
    selected
  }) => {
    if (typeof onPageChange === "function") {
      onPageChange(selected + 1);
    }
  };
  const showPagination = typeof onPageChange === "function" && totalCount > limit;
  const formatAddress = address => {
    if (!address || typeof address !== "string") return "-";
    if (address.length <= 12) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };
  const formatDepositAddressDisplay = address => {
    if (!address || typeof address !== "string") return "-";
    const trimmed = address.trim();
    if (trimmed.length <= 28) return trimmed;
    return `${trimmed.slice(0, 12)}....${trimmed.slice(-12)}`;
  };
  const getDisplayStatus = status => {
    const normalized = String(status || "").toLowerCase();
    return normalized === "active" ? "active" : "inactive";
  };
  const getWalletTypeLabel = data => {
    const raw = String(data?.walletType ?? data?.type ?? "").trim();
    if (!raw) return "-";
    const k = raw.toLowerCase();
    if (k.includes("metamask")) return "MetaMask";
    if (k.includes("bitgo")) return "BitGo";
    if (k.includes("trust")) return "Trust Wallet";
    if (k.includes("walletconnect")) return "WalletConnect";
    if (k.includes("coinbase")) return "Coinbase Wallet";
    if (k.includes("non_custodial") || k === "nc" || k.includes("noncustodial")) {
      return "Non-Custodial";
    }
    if (k.includes("custodial")) return "Custodial";
    return raw;
  };
  const getWalletTypeIcon = data => {
    const raw = String(data?.walletType ?? data?.type ?? "").trim().toLowerCase();
    if (raw.includes("metamask")) return metamaskIcon;
    if (raw.includes("bitgo")) return bitgoIcon;
    return null;
  };
  const formatTokenAmount = value => {
    if (value == null || value === "") return "-";
    const n = Number(value);
    if (!Number.isFinite(n)) return displayField(value);
    return n.toLocaleString("en-US", {
      maximumFractionDigits: 18
    });
  };
  const openWalletTokens = data => {
    const wallet_name = data?.walletName ?? data?.wallet_name ?? "";
    const tokens = resolveTokensForModal(data);
    const fromTokenList = Array.isArray(data?.tokenList) && data.tokenList.length > 0;
    if (tokens.length > 0) {
      const walletAddress = typeof data?.address === "string" && data.address.trim() || typeof tokens[0]?.address === "string" && tokens[0].address.trim() || "";
      const parentCoinRaw = tokens[0]?.parentCoin ?? tokens[0]?.parent_coin ?? "";
      const parentCoin = typeof parentCoinRaw === "string" ? parentCoinRaw.trim().toUpperCase() : parentCoinRaw != null && parentCoinRaw !== "" ? String(parentCoinRaw).trim().toUpperCase() : "";
      setTokenModal({
        show: true,
        walletLabel: wallet_name || formatAddress(data?.address),
        networkLabel: displayField(data?.network ?? data?.network_name),
        chainLabel: displayField(data?.chain ?? data?.chainName ?? data?.blockchain),
        walletAddress,
        parentCoin,
        tokens,
        tokenShape: fromTokenList ? "tokenList" : "assetList"
      });
      return;
    }
    toastMsg.error("No token list found for this wallet in the current response.");
  };
  const closeTokenModal = () => setTokenModal({
    show: false,
    walletLabel: "",
    networkLabel: "",
    chainLabel: "",
    walletAddress: "",
    parentCoin: "",
    tokens: [],
    tokenShape: "assetList"
  });
  const openDepositModal = data => {
    const address = typeof data?.address === "string" ? data.address.trim() : "";
    if (!address) {
      toastMsg.error("No deposit address available for this wallet.");
      return;
    }
    setDepositModal({
      show: true,
      address,
      walletLabel: data?.walletName ?? data?.wallet_name ?? "Wallet"
    });
  };
  const closeDepositModal = () => setDepositModal({
    show: false,
    address: "",
    walletLabel: ""
  });
  const openWithdrawModal = data => {
    const ctx = buildWithdrawContext(data);
    if (!ctx.nativeCoin) {
      toastMsg.error("Could not determine coin for this wallet.");
      return;
    }
    if (ctx.nativeBalance <= MIN_WITHDRAWABLE_BALANCE && ctx.childTokenOptions.length === 0) {
      toastMsg.error("No balance available to withdraw.");
      return;
    }
    const startWithChild = ctx.nativeBalance <= MIN_WITHDRAWABLE_BALANCE && ctx.childTokenOptions.length > 0;
    const initialCoin = startWithChild ? ctx.childTokenOptions[0].coin : ctx.nativeCoin;
    setUseTokenSelect(startWithChild);
    setWithdrawFormCoin(initialCoin);
    withdrawFormik.resetForm({
      values: {
        coin: initialCoin,
        address: "",
        amount: ""
      }
    });
    setWithdrawModal({
      show: true,
      walletLabel: data?.walletName ?? data?.wallet_name ?? "Wallet",
      mainCoin: ctx.nativeCoin,
      mainBalance: ctx.nativeBalance,
      childTokenOptions: ctx.childTokenOptions,
      totalTokenCount: ctx.totalTokenCount,
      network: ctx.network,
      chain: ctx.chain,
      walletType: ctx.walletType
    });
  };
  const closeWithdrawModal = () => {
    setWithdrawModal({
      show: false,
      walletLabel: "",
      mainCoin: "",
      mainBalance: 0,
      childTokenOptions: [],
      totalTokenCount: 0,
      network: "",
      chain: "",
      walletType: ""
    });
    setUseTokenSelect(false);
    setWithdrawFormCoin("");
    withdrawFormik.resetForm();
  };
  const handleTokenSelectToggle = checked => {
    setUseTokenSelect(checked);
    if (checked) {
      const first = withdrawModal.childTokenOptions[0]?.coin ?? "";
      setWithdrawFormCoin(first);
      withdrawFormik.setFieldValue("coin", first);
    } else {
      setWithdrawFormCoin(withdrawModal.mainCoin);
      withdrawFormik.setFieldValue("coin", withdrawModal.mainCoin);
    }
    withdrawFormik.setFieldValue("amount", "");
    withdrawFormik.setFieldTouched("amount", false);
  };
  const handleWithdrawTokenChange = e => {
    const coin = e.target.value;
    setWithdrawFormCoin(coin);
    withdrawFormik.setFieldValue("coin", coin);
    withdrawFormik.setFieldValue("amount", "");
    withdrawFormik.setFieldTouched("amount", false);
  };
  const showWithdrawTokenToggle = withdrawModal.childTokenOptions.length > 0;
  const withdrawableChildCount = withdrawModal.childTokenOptions.length;
  const tokenModalFieldsAsset = ["Asset", "Network", "Chain", "Balance", "$ value"];
  const tokenModalFieldsBitGo = ["Coin", "Balance"];
  return <>
      <div className="walletTable">
        <CommonTable className="innerCommontable" fields={Tableheading}>
          {rows.length > 0 && rows.map((data, index) => {
          const status = getDisplayStatus(data?.status);
          const tokenCount = resolveTokenCount(data);
          const networkCell = displayField(data?.network ?? data?.network_name ?? data?.networkType);
          const chainCell = displayField(data?.chain ?? data?.chainName ?? data?.blockchain);
          const walletTypeRaw = String(data?.walletType ?? "").trim().toLowerCase();
          const isMetaMask = walletTypeRaw.includes("metamask");
          const walletTypeCell = getWalletTypeLabel(data);
          const walletTypeIcon = getWalletTypeIcon(data);
          const rowKey = data?.wallet_id ?? data?.walletId ?? data?.address ?? `${networkCell}-${chainCell}-${index}`;
          return <tr key={rowKey}>
                       <td>
                      <span className="walletTypeCell">
                        {walletTypeIcon ? <img className="walletTypeCell__icon" src={walletTypeIcon} alt={`${walletTypeCell} icon`} /> : null}
                        <span className="walletTypeCell__label">
                          {walletTypeCell}
                        </span>
                      </span>
                    </td>
                    <td>
                      {data?.walletName || data?.wallet_name || "-"}
                    </td>
                    <td>{networkCell}</td>
                    <td>{chainCell}</td>
                 

                    <td>
                      {formatAddress(data?.address)}{" "}
                      <span className="copyIcon ms-3 ms-md-4" onClick={() => copyToClipboard(data?.address || "")}>
                        <CopyIcon />
                      </span>
                    </td>
                    <td>
                      <div className="walletTokenCell">
                        <span className="walletTokenCell__count" title="Number of tokens">
                          {tokenCount}
                        </span>
                        {tokenCount > 0 && <button type="button" className="walletViewTokensBtn" onClick={() => openWalletTokens(data)} aria-label={`View all ${tokenCount} tokens for this wallet`}>
                            <span className="walletViewTokensBtn__icon" aria-hidden>
                              <EyeIcon />
                            </span>
                            <span className="walletViewTokensBtn__label">
                              View
                            </span>
                          </button>}
                      </div>
                    </td>
                    <td>
                      <div className={`walletStatus ${status}`}>
                        {status}
                      </div>
                    </td>
                    <td>
                      <div className="walletActionCell">
                        <button type="button" className="walletActionBtn walletActionBtn--deposit" onClick={() => openDepositModal(data)}>
                          Deposit
                        </button>
                        {!isMetaMask && <button type="button" className="walletActionBtn walletActionBtn--withdraw" onClick={() => openWithdrawModal(data)}>
                            Withdraw
                          </button>}
                      </div>
                    </td>
                  </tr>;
        })}
        </CommonTable>

        <Modal show={tokenModal.show} onHide={closeTokenModal} centered size={tokenModal.tokenShape === "tokenList" ? "lg" : "xl"} className="walletTokensModal" dialogClassName={tokenModal.tokenShape === "tokenList" ? "walletTokensModal__dialog walletTokensModal__dialog--compact" : "walletTokensModal__dialog"} contentClassName="walletTokensModal__content" aria-labelledby="wallet-tokens-modal-title">
          <button type="button" className="walletTokensModal__close btn-close" aria-label="Close" onClick={closeTokenModal} />
          <Modal.Header closeButton={false} className="walletTokensModal__header">
            <div className="walletTokensModal__headerText">
              <span className="walletTokensModal__eyebrow">Wallet tokens</span>
              {tokenModal.walletLabel ? <Modal.Title as="h2" className="walletTokensModal__subtitle" id="wallet-tokens-modal-title">
                 {/* Wallet Name: {tokenModal.walletLabel}  */}
                </Modal.Title> : null}
            </div>
          </Modal.Header>
          <Modal.Body className="walletTokensModal__body">
            {tokenModal.tokenShape === "tokenList" ? <>
                {tokenModal.parentCoin || tokenModal.walletAddress || tokenModal.networkLabel !== "-" || tokenModal.chainLabel !== "-" ? <div className="walletTokensModal__detailsCard">
                    <span className="walletTokensModal__detailsLabel">
                      Wallet details
                    </span>
                    <div className="walletTokensModal__walletMeta">
                    {tokenModal.parentCoin ? <span className="walletTokensModal__headerMetaItem">
                        <strong>Parent Coin:</strong>{" "}
                        <span className="walletTokensModal__headerMetaValue">
                          {tokenModal.parentCoin}
                        </span>
                      </span> : null}
                    {tokenModal.networkLabel !== "-" ? <span className="walletTokensModal__headerMetaItem">
                        <strong>Network:</strong>{" "}
                        <span className="walletTokensModal__headerMetaValue">
                          {tokenModal.networkLabel}
                        </span>
                      </span> : null}
                    {tokenModal.chainLabel !== "-" ? <span className="walletTokensModal__headerMetaItem">
                        <strong>Chain:</strong>{" "}
                        <span className="walletTokensModal__headerMetaValue">
                          {tokenModal.chainLabel}
                        </span>
                      </span> : null}
                    {tokenModal.walletAddress ? <span className="walletTokensModal__headerMetaItem walletTokensModal__headerMetaItem--address">
                        <strong>Address:</strong>{" "}
                        <span className="walletTokensModal__headerMetaValue">
                          {formatAddress(tokenModal.walletAddress)}
                        </span>
                        <span className="copyIcon ms-2" role="button" tabIndex={0} onClick={() => copyToClipboard(tokenModal.walletAddress)} onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      copyToClipboard(tokenModal.walletAddress);
                    }
                  }}>
                          <CopyIcon />
                        </span>
                      </span> : null}
                    </div>
                  </div> : null}
                <div className="walletTokensModal__tableCard">
                  <span className="walletTokensModal__detailsLabel">
                    Token balances
                  </span>
                  <div className="walletTokensModal__tableWrap">
                  <CommonTable responsive={false} className="innerCommontable" fields={tokenModalFieldsBitGo}>
                    {tokenModal.tokens.map((t, i) => <tr key={`${String(t?.coin)}-${i}`}>
                        <td>{displayField(t?.coin ?? t?.symbol)}</td>
                        <td>{formatTokenAmount(t?.spendableBalance)}</td>
                      </tr>)}
                  </CommonTable>
                  </div>
                </div>
              </> : <div className="walletTokensModal__tableWrap">
                <CommonTable className="innerCommontable" fields={tokenModalFieldsAsset}>
                  {tokenModal.tokens.map((t, i) => <tr key={t?.asset_id ?? t?.symbol ?? i}>
                      <td>{t?.asset_name ?? t?.symbol ?? "-"}</td>
                      <td>
                        {t?.network ?? t?.network_name ?? tokenModal.networkLabel}
                      </td>
                      <td>
                        {t?.chain ?? t?.chainName ?? t?.blockchain ?? tokenModal.chainLabel}
                      </td>
                      <td>
                        {t?.available_balance != null ? Number(t.available_balance).toLocaleString("en-US", {
                    maximumFractionDigits: 12
                  }) : "-"}
                      </td>
                      <td>
                        {t?.available_usd_value != null ? formatPortfolioUsdAmount(t.available_usd_value) : "-"}
                      </td>
                    </tr>)}
                </CommonTable>
              </div>}
          </Modal.Body>
        </Modal>

        <Modal show={depositModal.show} onHide={closeDepositModal} centered className="walletDepositModal" dialogClassName="walletDepositModal__dialog" contentClassName="walletDepositModal__content" aria-labelledby="wallet-deposit-modal-title">
          <button type="button" className="walletDepositModal__close btn-close" aria-label="Close" onClick={closeDepositModal} />
          <Modal.Header closeButton={false} className="walletDepositModal__header">
            <div className="walletDepositModal__headerText">
              <span className="walletDepositModal__eyebrow">Deposit funds</span>
              <Modal.Title as="h2" className="walletDepositModal__title" id="wallet-deposit-modal-title">
                {depositModal.walletLabel || "Wallet"}
              </Modal.Title>
            </div>
          </Modal.Header>
          <Modal.Body className="walletDepositModal__body">
            {depositModal.address ? <>
                <p className="walletDepositModal__lead">
                  Scan the QR code or copy your wallet address to deposit.
                </p>
                <div className="walletDepositModal__qrCard">
                  <div className="walletDepositModal__qrWrap">
                    <QRCode value={depositModal.address} size={240} quietZone={10} bgColor="#ffffff" fgColor="#0f172a" />
                  </div>
                </div>
                <div className="walletDepositModal__addressCard">
                  <span className="walletDepositModal__addressLabel">
                    Wallet address
                  </span>
                  <div className="walletDepositModal__addressRow">
                    <span className="walletDepositModal__address" title={depositModal.address}>
                      {formatDepositAddressDisplay(depositModal.address)}
                    </span>
                    <button type="button" className="walletDepositModal__copyBtn" aria-label="Copy deposit address" onClick={() => copyToClipboard(depositModal.address)}>
                      <CopyIcon />
                      <span>Copy</span>
                    </button>
                  </div>
                </div>
              </> : <p className="walletDepositModal__empty">No address available.</p>}
          </Modal.Body>
        </Modal>

        <Modal show={withdrawModal.show} onHide={closeWithdrawModal} centered className="walletWithdrawModal" dialogClassName="walletWithdrawModal__dialog" contentClassName="walletWithdrawModal__content" aria-labelledby="wallet-withdraw-modal-title">
          <button type="button" className="walletWithdrawModal__close btn-close" aria-label="Close" onClick={closeWithdrawModal} />
          <Modal.Header closeButton={false} className="walletWithdrawModal__header">
            <div className="walletWithdrawModal__headerText">
              <span className="walletWithdrawModal__eyebrow">Withdraw funds</span>
              <Modal.Title as="h2" className="walletWithdrawModal__title" id="wallet-withdraw-modal-title">
                {withdrawModal.walletLabel || "Wallet"}
              </Modal.Title>
            </div>
          </Modal.Header>
          <Modal.Body className="walletWithdrawModal__body">
            <form className="walletWithdrawModal__form" onSubmit={withdrawFormik.handleSubmit} noValidate>
              {(withdrawModal.network !== "-" || withdrawModal.chain !== "-") && <p className="walletWithdrawModal__meta">
                  {withdrawModal.network !== "-" ? withdrawModal.network : null}
                  {withdrawModal.network !== "-" && withdrawModal.chain !== "-" ? " · " : null}
                  {withdrawModal.chain !== "-" ? withdrawModal.chain : null}
                  {withdrawModal.totalTokenCount > 0 ? <span className="walletWithdrawModal__metaTokens">
                      {" "}
                      · {withdrawModal.totalTokenCount} token
                      {withdrawModal.totalTokenCount === 1 ? "" : "s"}
                      {withdrawableChildCount > 0 ? ` (${withdrawableChildCount} with balance)` : ""}
                    </span> : null}
                </p>}

              <div className="walletWithdrawModal__balanceCard">
                <span className="walletWithdrawModal__balanceLabel">
                  {useTokenSelect ? "Selected token balance" : "Native balance"}
                </span>
                <p className="walletWithdrawModal__balanceValue">
                  {formatWithdrawBalance(availableWithdrawBalance)}{" "}
                  <span className="walletWithdrawModal__balanceCoin">
                    {withdrawBalanceCoinLabel || "—"}
                  </span>
                </p>
                {!useTokenSelect ? <span className="walletWithdrawModal__balanceHint">
                    {withdrawModal.walletType === "native" ? "Native coin on this wallet" : "Main coin for this wallet"}
                    {withdrawModal.mainBalance <= MIN_WITHDRAWABLE_BALANCE && withdrawableChildCount > 0 ? " — enable child token below to withdraw token balance" : ""}
                  </span> : <span className="walletWithdrawModal__balanceHint">
                    Child token on {withdrawModal.mainCoin} wallet
                  </span>}
              </div>

              {showWithdrawTokenToggle ? <Form.Group className="walletWithdrawModal__tokenSwitch checkbox_input" controlId="withdraw_use_token_select">
                  <Form.Check type="switch" id="withdraw_use_token_select" label={`Withdraw child token (${withdrawableChildCount} available)`} checked={useTokenSelect} onChange={e => handleTokenSelectToggle(e.target.checked)} />
                </Form.Group> : null}

              {useTokenSelect ? <Form.Group className="walletWithdrawModal__selectGroup customInput">
                  <Form.Label htmlFor="withdraw_token_select">Token</Form.Label>
                  <Form.Select id="withdraw_token_select" name="coin" value={withdrawFormik.values.coin} onChange={handleWithdrawTokenChange} onBlur={withdrawFormik.handleBlur} isInvalid={withdrawFormik.touched.coin && Boolean(withdrawFormik.errors.coin)}>
                    {withdrawModal.childTokenOptions.map(opt => <option key={opt.coin} value={opt.coin}>
                        {formatChildWithdrawTokenLabel(opt)} —{" "}
                        {formatWithdrawBalance(opt.balance)}
                      </option>)}
                  </Form.Select>
                  {withdrawFormik.touched.coin && withdrawFormik.errors.coin ? <div className="text-danger small mt-1">
                      {withdrawFormik.errors.coin}
                    </div> : null}
                </Form.Group> : <CustomInput label="Coin" id="withdraw_coin" name="coin" type="text" value={withdrawFormik.values.coin} disabled readOnly onChange={withdrawFormik.handleChange} onBlur={withdrawFormik.handleBlur} isInvalid={withdrawFormik.touched.coin && Boolean(withdrawFormik.errors.coin)} error={withdrawFormik.touched.coin && withdrawFormik.errors.coin ? <div className="text-danger small mt-1">
                        {withdrawFormik.errors.coin}
                      </div> : null} />}

              <CustomInput label="Address" id="withdraw_address" name="address" type="text" placeholder="Paste withdrawal address" maxLength={WITHDRAW_ADDRESS_MAX_LENGTH} value={withdrawFormik.values.address} onChange={withdrawFormik.handleChange} onBlur={withdrawFormik.handleBlur} isInvalid={withdrawFormik.touched.address && Boolean(withdrawFormik.errors.address)} error={withdrawFormik.touched.address && withdrawFormik.errors.address ? <div className="text-danger small mt-1">
                      {withdrawFormik.errors.address}
                    </div> : null} />
              <CustomInput label="Amount" id="withdraw_amount" name="amount" type="text" placeholder="0.0" value={withdrawFormik.values.amount} maxLength={WITHDRAW_AMOUNT_MAX_LENGTH} onChange={e => {
              const next = normalizeWithdrawAmountInput(e.target.value);
              if (next !== null) {
                withdrawFormik.setFieldValue("amount", next);
              }
            }} onBlur={e => {
              const finalized = finalizeWithdrawAmountOnBlur(withdrawFormik.values.amount);
              if (finalized !== withdrawFormik.values.amount) {
                withdrawFormik.setFieldValue("amount", finalized);
              }
              withdrawFormik.handleBlur(e);
            }} isInvalid={withdrawFormik.touched.amount && Boolean(withdrawFormik.errors.amount)} error={withdrawFormik.touched.amount && withdrawFormik.errors.amount ? <div className="text-danger small mt-1">
                      {withdrawFormik.errors.amount}
                    </div> : null} smallText={`Max: ${formatWithdrawBalance(availableWithdrawBalance)}`} />
              <div className="walletWithdrawModal__actions">
                <button type="button" className="walletWithdrawModal__cancelBtn" onClick={closeWithdrawModal} disabled={withdrawSubmitting}>
                  Cancel
                </button>
                <CommonButton title={withdrawSubmitting ? "Please Wait..." : "Submit Withdraw"} type="submit" className="walletWithdrawModal__submitBtn" disabled={withdrawSubmitting || !withdrawFormik.isValid} />
              </div>
            </form>
          </Modal.Body>
        </Modal>

        <div className="text-end w-100">
          {showPagination && <ReactPagination handlePageChange={handlePageChange} activityList={totalCount} limit={limit} forcePage={page - 1} />}
        </div>
      </div>
    </>;
};
export default WalletTable;